import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { API, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PropertyContextHeader } from "@/components/PropertyContextHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AiDisclosure } from "@/components/AiDisclosure";
import { EvidenceCitedStatements } from "@/components/EvidenceCitedStatements";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { FeedbackPrompt } from "@/context/FeedbackContext";
import { DisputeChargeModule } from "@/components/DisputeChargeModule";
import { DeductionAnalysis } from "@/components/DeductionAnalysis";
import { HelpHint } from "@/components/HelpHint";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, ArrowRight, Scale, Loader2, AlertTriangle, FileText, Download,
  RefreshCw, Info, Check, Share2, Copy, CheckCircle2, ScanSearch, ShieldCheck,
  Camera, MessageSquare, Receipt, Wrench,
} from "lucide-react";
import DOMPurify from "dompurify";

const CLASS_META = {
  NO_CHANGE: { label: "No Change", cls: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]" },
  NORMAL_WEAR: { label: "Normal Wear", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  NEW_DAMAGE: { label: "New Damage", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]" },
};

function renderMarkdown(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => DOMPurify.sanitize(esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/(?<!\*)\*(?!\*)(.+?)\*/g, "<em>$1</em>"));
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-3" />;
    if (t.startsWith("### ")) return <h3 key={i} className="font-heading font-semibold text-navy mt-3" dangerouslySetInnerHTML={{ __html: inline(t.slice(4)) }} />;
    if (t.startsWith("## ")) return <h2 key={i} className="font-heading font-semibold text-navy mt-3 text-lg" dangerouslySetInnerHTML={{ __html: inline(t.slice(3)) }} />;
    if (t.startsWith("# ")) return <h1 key={i} className="font-heading font-bold text-navy mt-3 text-xl" dangerouslySetInnerHTML={{ __html: inline(t.slice(2)) }} />;
    return <p key={i} className="text-sm text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
}

export default function DisputeLetter() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [letters, setLetters] = useState([]);
  const [step, setStep] = useState(1);

  const [selected, setSelected] = useState(() => new Set());
  const [auditId, setAuditId] = useState(null);
  const [auditFlagged, setAuditFlagged] = useState(0);
  const [includeAudit, setIncludeAudit] = useState(true);
  const [deductionAnalysisId, setDeductionAnalysisId] = useState(null);
  const onAudit = useCallback((a) => {
    if (a?.status === "completed") {
      setAuditId(a.audit_id);
      const f = (a.line_items || []).filter((li) => li.flagged).length;
      setAuditFlagged(f);
      setIncludeAudit(f > 0);
    }
  }, []);
  const [form, setForm] = useState({ tenant_name: "", tenant_address: "", landlord_name: "", landlord_address: "", deposit_amount: "", amount_withheld: "", deduction_reasons: "", move_out_date: "" });

  const [genState, setGenState] = useState("idle"); // idle | pending | error
  const [genError, setGenError] = useState("");
  const [doc, setDoc] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [saveState, setSaveState] = useState("saved"); // saved | saving
  const saveTimer = useRef(null);

  const [pdfState, setPdfState] = useState("idle"); // idle | pending
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [assembled, setAssembled] = useState("");

  const loadAssembled = useCallback(async () => {
    if (!doc?.letter_id) return;
    try {
      const { data } = await api.get(`/dispute-letters/id/${doc.letter_id}/assembled`);
      setAssembled(data.body_markdown || "");
    } catch { /* ignore */ }
  }, [doc]);

  useEffect(() => { if (doc?.structured) loadAssembled(); }, [doc, loadAssembled]);

  useEffect(() => {
    api.get(`/properties/${propertyId}/evidence-coverage`)
      .then(({ data }) => setCoverage(data))
      .catch(() => {});
  }, [propertyId]);

  const loadLetters = useCallback(async () => {
    const { data } = await api.get(`/dispute-letters/${propertyId}`);
    setLetters(data);
  }, [propertyId]);

  const load = useCallback(async () => {
    const [{ data: jobs }] = await Promise.all([api.get(`/comparisons/${propertyId}`)]);
    const completed = jobs.filter((j) => j.status === "completed" && j.result);
    setResults(completed);
    // Pre-check NO_CHANGE + NORMAL_WEAR
    const pre = new Set(completed.filter((j) => j.result.classification !== "NEW_DAMAGE").map((j) => j.result.result_id));
    setSelected(pre);
    await loadLetters();
    setLoading(false);
  }, [propertyId, loadLetters]);

  useEffect(() => { load(); }, [load]);

  const toggle = (rid) => setSelected((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });

  // ---- validation
  const dep = parseFloat(form.deposit_amount);
  const wit = parseFloat(form.amount_withheld);
  const amountsValid = dep > 0 && wit > 0 && wit <= dep;
  const step2Valid = form.tenant_name && form.tenant_address && form.landlord_name && form.landlord_address && form.move_out_date && amountsValid;

  const includedJobs = results.filter((j) => j.result && selected.has(j.result.result_id));
  const trustTotal = includedJobs.length;
  const trustLoc = includedJobs.filter((j) => j.move_out_custody?.location_verified === "verified").length;
  const trustHashed = includedJobs.filter((j) => j.move_out_custody?.sha256 || j.move_out_custody?.has_hash).length;

  const generate = async () => {
    setStep(3);
    setGenState("pending");
    setGenError("");
    try {
      const { data } = await api.post(`/dispute-letters/generate`, {
        property_id: propertyId,
        tenant_name: form.tenant_name, tenant_address: form.tenant_address,
        landlord_name: form.landlord_name, landlord_address: form.landlord_address,
        deposit_amount: dep, amount_withheld: wit,
        deduction_reasons: form.deduction_reasons || null,
        move_out_date: form.move_out_date,
        included_result_ids: Array.from(selected),
        deduction_audit_id: includeAudit && auditId ? auditId : null,
        deduction_analysis_id: deductionAnalysisId,
      });
      setDoc(data);
      setMarkdown(data.letter_body_markdown);
      setShareUrl(null);
      setGenState("idle");
      loadLetters();
    } catch (e) {
      setGenError(e?.response?.data?.detail || "Could not draft the letter.");
      setGenState("error");
    }
  };

  const scheduleSave = (value) => {
    setMarkdown(value);
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/dispute-letters/id/${doc.letter_id}`, { letter_body_markdown: value });
        setDoc(data);
        setShareUrl(null);
        setSaveState("saved");
      } catch { setSaveState("saved"); }
    }, 800);
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate will overwrite your manual edits. Continue?")) return;
    await generate();
  };

  const generatePdf = async () => {
    setPdfState("pending");
    try {
      const { data } = await api.post(`/dispute-letters/id/${doc.letter_id}/pdf`);
      setDoc(data);
      loadLetters();
    } catch (e) {
      if (e?.response?.status === 402) {
        // Global 402 interceptor opens the UpgradeModal — no local modal needed.
      } else if (e?.response?.status === 409) {
        const d = e?.response?.data?.detail;
        toast.error((d && typeof d === "object" ? d.message : d) || "Some statements need attention before export.");
        setStep(3);
      } else {
        const d = e?.response?.data?.detail;
        toast.error((d && typeof d === "object" ? d.message : d) || "Could not assemble the PDF.");
      }
    } finally {
      setPdfState("idle");
    }
  };

  const onGenerateClick = () => {
    generatePdf();
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/dispute-letters/id/${doc.letter_id}/pdf-file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "dispute-letter.pdf"; a.click();
      URL.revokeObjectURL(url);
      setPdfDownloaded(true);
    } catch { toast.error("Download failed"); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/dispute-letters/id/${doc.letter_id}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { toast.success("Share link ready"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link"); }
  };

  const startNew = () => {
    setDoc(null); setMarkdown(""); setShareUrl(null); setGenState("idle"); setStep(1);
    setDeductionAnalysisId(null);
    const pre = new Set(results.filter((j) => j.result.classification !== "NEW_DAMAGE").map((j) => j.result.result_id));
    setSelected(pre);
  };

  const [converting, setConverting] = useState(false);
  const openLetter = (l) => {
    setDoc(l); setMarkdown(l.letter_body_markdown || ""); setGenState("idle"); setShareUrl(null); setStep(3);
  };
  const convertLetter = async () => {
    if (!doc?.letter_id) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/dispute-letters/id/${doc.letter_id}/convert`);
      const next = data.new_letter_id
        ? (await api.get(`/dispute-letters/${propertyId}`)).data.find((l) => l.letter_id === data.new_letter_id)
        : data;
      setDoc(next || { ...doc, structured: true });
      await loadLetters();
      toast.success(data.new_letter_id ? "Created an editable revision of this letter." : "Converted to editable statements.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't convert this letter.");
    } finally { setConverting(false); }
  };

  if (loading) return <AppShell><Skeleton className="h-96 rounded-2xl" /></AppShell>;

  const pdfStale = doc && doc.status === "draft" && letters.find((l) => l.letter_id === doc.letter_id)?.pdf_storage_url === null && doc.pdf_storage_url === null;

  return (
    <AppShell>
      <PropertyContextHeader propertyId={propertyId} />
      <div className="flex items-center gap-2">
        <Scale size={24} className="text-brand" />
        <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Dispute Letter</h1>
        <HelpHint topic="disputeLetter" className="ml-auto" />
      </div>

      {coverage && (coverage.photos + coverage.messages + coverage.payments + coverage.maintenance) > 0 && (
        <div data-testid="evidence-coverage-badge" className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
          <span className="text-sm font-medium text-navy flex items-center gap-1.5"><ShieldCheck size={15} className="text-brand" /> Evidence linked to this dispute:</span>
          {[
            { n: coverage.photos, label: "photos", one: "photo", Icon: Camera },
            { n: coverage.messages, label: "messages", one: "message", Icon: MessageSquare },
            { n: coverage.payments, label: "payments", one: "payment", Icon: Receipt },
            { n: coverage.maintenance, label: "maintenance issues", one: "maintenance issue", Icon: Wrench },
          ].filter((c) => c.n > 0).map((c) => (
            <span key={c.label} data-testid={`coverage-${c.label.split(" ")[0]}`} className="inline-flex items-center gap-1.5 rounded-full bg-amber-soft text-[#92400E] text-xs font-semibold px-2.5 py-1">
              <c.Icon size={13} /> {c.n} {c.n === 1 ? c.one : c.label}
            </span>
          ))}
        </div>
      )}

      {/* Prior letters */}
      {letters.length > 0 && (
        <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="letters-list">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-navy">Your letters</h2>
            <Button data-testid="start-new-letter-btn" size="sm" variant="outline" onClick={startNew} className="rounded-lg">Start new letter</Button>
          </div>
          <div className="mt-3 divide-y divide-[#F1F5F9]">
            {letters.map((l) => (
              <div key={l.letter_id} className="flex items-center justify-between py-2 text-sm" data-testid={`letter-row-${l.letter_id}`}>
                <span className="text-[#475569]">{new Date(l.created_at).toLocaleDateString()} · <span className={l.status === "finalized" ? "text-[#177245] font-medium" : "text-[#92400E]"}>{l.status}</span></span>
                <div className="flex items-center gap-3">
                  <button data-testid={`letter-continue-${l.letter_id}`} onClick={() => openLetter(l)} className="text-brand font-medium">{l.structured ? "Continue editing" : "Open"}</button>
                  {l.status === "finalized" && l.letter_id === doc?.letter_id && (
                    <button onClick={downloadPdf} className="text-brand font-medium flex items-center gap-1"><Download size={14} /> PDF</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2" data-testid="wizard-steps">
        {["Evidence", "Details", "Draft", "PDF"].map((s, i) => (
          <div key={s} className={`flex items-center gap-2 ${i + 1 === step ? "text-navy font-semibold" : "text-[#94A3B8]"}`}>
            <span className={`grid place-items-center h-6 w-6 rounded-full text-xs font-bold ${i + 1 <= step ? "bg-brand text-white" : "bg-[#E2E8F0] text-[#94A3B8]"}`}>{i + 1}</span>
            <span className="text-sm hidden sm:inline">{s}</span>
            {i < 3 && <ArrowRight size={14} className="text-[#CBD5E1]" />}
          </div>
        ))}
      </div>

      {/* STEP 1 — Evidence */}
      {step === 1 && (
        <div className="mt-6" data-testid="step-evidence">
          <div className="mb-6">
            <DeductionAnalysis
              propertyId={propertyId}
              onUseAnalysis={(id, total) => {
                setDeductionAnalysisId(id);
                setForm((f) => ({ ...f, amount_withheld: total ? String(total) : f.amount_withheld }));
                setStep(2);
              }}
            />
          </div>
          {results.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-8 text-center" data-testid="evidence-empty">
              <span className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-amber-soft text-amber"><ScanSearch size={26} /></span>
              <h2 className="mt-4 font-heading text-xl font-semibold text-navy">Run a photo comparison first</h2>
              <p className="mt-2 text-[#475569]">You need completed move-in vs move-out comparisons before drafting a letter.</p>
              <Button data-testid="go-compare-btn" onClick={() => navigate(`/compare/${propertyId}`)} className="mt-5 rounded-xl bg-brand hover:bg-brand-hover text-white">Go to Comparison Report</Button>
            </div>
          ) : (
            <>
              <p className="text-[#475569] text-sm">Select the findings to reference. No-change and normal-wear findings support your dispute and are pre-selected.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {results.map((j) => {
                  const rid = j.result.result_id;
                  const meta = CLASS_META[j.result.classification];
                  const checked = selected.has(rid);
                  return (
                    <button key={rid} data-testid={`evidence-card-${rid}`} onClick={() => toggle(rid)}
                      className={`text-left rounded-2xl border-2 p-3 transition-all ${checked ? "border-amber bg-amber-soft" : "border-[#E2E8F0] bg-white hover:border-[#CBD5E1]"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-heading font-semibold text-navy flex items-center gap-2">
                          {checked ? <CheckCircle2 size={16} className="text-amber" /> : <span className="h-4 w-4 rounded-full border-2 border-[#CBD5E1]" />} {j.room_label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>
                          {meta.label}
                          {j.result.classification === "NEW_DAMAGE" && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild><Info size={12} data-testid={`damage-warn-${rid}`} /></TooltipTrigger>
                              <TooltipContent>The AI classified this photo as new damage — review it yourself before including it.</TooltipContent></Tooltip></TooltipProvider>
                          )}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <img src={fileUrl(j.move_in_image)} alt="" className="aspect-video object-cover rounded-lg border border-[#E2E8F0]" />
                        <img src={fileUrl(j.move_out_image)} alt="" className="aspect-video object-cover rounded-lg border border-[#E2E8F0]" />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Optional: review the landlord's itemized deductions */}
              <div className="mt-6">
                <DisputeChargeModule propertyId={propertyId} onAudit={onAudit} />
                {auditId && auditFlagged > 0 && (
                  <label data-testid="include-audit-toggle" className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
                    <span className="text-sm text-[#334155]">Include deduction review findings in the letter
                      <span className="block text-xs text-[#94A3B8]">Adds a substantiation-request paragraph for each of the {auditFlagged} flagged charge{auditFlagged !== 1 ? "s" : ""}.</span>
                    </span>
                    <Switch checked={includeAudit} onCheckedChange={setIncludeAudit} data-testid="include-audit-switch" />
                  </label>
                )}
              </div>

              <Button data-testid="step1-next-btn" disabled={selected.size === 0} onClick={() => setStep(2)} className="mt-6 rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white disabled:opacity-50">
                Next <ArrowRight size={16} className="ml-2" />
              </Button>
            </>
          )}
        </div>
      )}

      {/* STEP 2 — Details */}
      {step === 2 && (
        <div className="mt-6 max-w-2xl" data-testid="step-details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" testId="dl-tenant-name" value={form.tenant_name} onChange={(v) => setForm({ ...form, tenant_name: v })} />
            <Field label="Your address" testId="dl-tenant-address" value={form.tenant_address} onChange={(v) => setForm({ ...form, tenant_address: v })} />
            <Field label="Landlord name" testId="dl-landlord-name" value={form.landlord_name} onChange={(v) => setForm({ ...form, landlord_name: v })} />
            <Field label="Landlord address" testId="dl-landlord-address" value={form.landlord_address} onChange={(v) => setForm({ ...form, landlord_address: v })} />
            <Field label="Deposit amount ($)" testId="dl-deposit" type="number" value={form.deposit_amount} onChange={(v) => setForm({ ...form, deposit_amount: v })} />
            <Field label="Amount withheld ($)" testId="dl-withheld" type="number" value={form.amount_withheld} onChange={(v) => setForm({ ...form, amount_withheld: v })} />
            <div className="sm:col-span-2">
              <Field label="Move-out date" testId="dl-moveout-date" type="date" value={form.move_out_date} onChange={(v) => setForm({ ...form, move_out_date: v })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-medium text-navy">Landlord's stated deduction reasons (optional)</Label>
              <Textarea data-testid="dl-reasons" value={form.deduction_reasons} onChange={(e) => setForm({ ...form, deduction_reasons: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Paraphrase what the landlord claimed…" />
            </div>
          </div>
          {form.deposit_amount && form.amount_withheld && !amountsValid && (
            <p className="mt-3 text-sm text-[#B91C1C]" data-testid="amounts-error">Amounts must be positive and the withheld amount cannot exceed the deposit.</p>
          )}
          <div className="mt-6 flex gap-3">
            <Button data-testid="step2-back-btn" variant="outline" onClick={() => setStep(1)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back</Button>
            <Button data-testid="step2-next-btn" disabled={!step2Valid} onClick={generate} className="rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white disabled:opacity-50">
              Draft letter <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3 — Generate & Edit */}
      {step === 3 && (
        <div className="mt-6" data-testid="step-draft">
          <div className="rounded-xl border border-amber-border bg-amber-soft/60 px-4 py-3 text-sm text-[#8A5A12]" data-testid="legal-notice">
            <div className="flex items-start gap-2">
              <Info size={16} className="mt-0.5 shrink-0" />
              <LegalDisclaimer keyName="dispute_letter" className="text-sm text-[#8A5A12]" />
            </div>
            <LegalDisclaimer keyName="ai_analysis" className="mt-2 pl-6 text-xs text-[#8A5A12]/90" />
          </div>

          {genState === "pending" && (
            <div className="mt-4" data-testid="draft-skeleton">
              <p className="text-[#475569] mb-3 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Drafting your letter… (typically 5–15 seconds)</p>
              <Skeleton className="h-80 rounded-xl" />
            </div>
          )}

          {genState === "error" && (
            <div className="mt-4 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5" data-testid="draft-error">
              <p className="text-sm font-medium text-[#B91C1C] flex items-center gap-2"><AlertTriangle size={16} /> {genError}</p>
              <Button data-testid="draft-retry-btn" onClick={generate} className="mt-3 rounded-xl bg-navy hover:bg-navy-hover text-white">Try again</Button>
            </div>
          )}

          {genState === "idle" && doc && (
            <>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-[#94A3B8]">{doc.structured ? "Built from your reviewed statements" : (saveState === "saving" ? "Saving…" : "Saved")}</span>
                <Button data-testid="regenerate-btn" size="sm" variant="outline" onClick={regenerate} className="rounded-lg"><RefreshCw size={14} className="mr-1.5" /> Regenerate</Button>
              </div>
              <AiDisclosure className="mt-2" />
              {doc.structured ? (
                <>
                  {/* Canonical model: edit statement-by-statement; preview is read-only and assembled
                      strictly from the active statements so it can never diverge. */}
                  <EvidenceCitedStatements letterId={doc.letter_id} onChange={loadAssembled} />
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={15} className="text-[#94A3B8]" />
                      <span className="text-sm font-medium text-navy">Assembled letter preview</span>
                      <span className="text-[10px] text-[#94A3B8]">read-only</span>
                    </div>
                    <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 overflow-y-auto" data-testid="letter-preview">{renderMarkdown(assembled)}</div>
                  </div>
                </>
              ) : (
                <>
                  {/* Legacy letter — require a one-time conversion into canonical statements before editing. */}
                  <div className="mt-4 rounded-2xl border border-amber-border bg-amber-soft/50 p-4" data-testid="legacy-convert-card">
                    <div className="flex items-start gap-2">
                      <ScanSearch size={18} className="text-amber mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-navy">Make this letter evidence-cited</p>
                        <p className="text-xs text-[#8A5A12] mt-1">Convert this letter into reviewable statements so each factual sentence can be confirmed or backed by a source. {(doc.status === "finalized" || doc.exported_at) ? "Your already-exported version stays untouched — this creates a new editable revision." : ""}</p>
                      </div>
                    </div>
                    <Button data-testid="legacy-convert-btn" disabled={converting} onClick={convertLetter} className="mt-3 rounded-xl bg-navy hover:bg-navy-hover text-white h-11">
                      {converting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Converting…</> : "Convert to editable statements"}
                    </Button>
                  </div>
                  <div className="mt-4">
                    <span className="text-sm font-medium text-navy">Current letter</span>
                    <div className="mt-2 rounded-xl border border-[#E2E8F0] bg-white p-5 overflow-y-auto" data-testid="letter-preview">{renderMarkdown(markdown)}</div>
                  </div>
                </>
              )}
              <div className="mt-6 flex gap-3">
                <Button data-testid="step3-back-btn" variant="outline" onClick={() => setStep(2)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back</Button>
                <Button data-testid="step3-next-btn" onClick={() => setStep(4)} className="rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">Continue to PDF <ArrowRight size={16} className="ml-2" /></Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP 4 — PDF & Download */}
      {step === 4 && doc && (
        <div className="mt-6 max-w-2xl" data-testid="step-pdf">
          {trustTotal > 0 && (
            <div data-testid="trust-badge" className="mb-4 rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] px-4 py-3 flex items-center gap-2.5">
              <ShieldCheck size={18} className="text-emerald-600 shrink-0" />
              <span className="text-sm text-[#334155]">
                <b>{trustLoc} of {trustTotal}</b> photos location-verified · <b>{trustHashed} of {trustTotal}</b> integrity-hashed
              </span>
            </div>
          )}
          {doc.status !== "finalized" || doc.pdf_storage_url === null ? (
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              {doc.pdf_storage_url === null && letters.find((l) => l.letter_id === doc.letter_id)?.pdf_storage_url && (
                <p className="mb-3 text-sm text-[#92400E]" data-testid="pdf-stale-note">Your letter changed — regenerate the PDF.</p>
              )}
              <p className="text-[#475569]">Assemble a print-ready PDF with your letter and side-by-side photo evidence for the {selected.size} selected finding{selected.size === 1 ? "" : "s"}.</p>
              <Button data-testid="generate-pdf-btn" onClick={onGenerateClick} disabled={pdfState === "pending"} className="mt-4 rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">
                {pdfState === "pending" ? <><Loader2 size={18} className="mr-2 animate-spin" /> Assembling PDF with {selected.size} evidence photos…</> : <><FileText size={18} className="mr-2" /> Generate PDF</>}
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="pdf-ready">
              <div className="flex items-center gap-2 text-[#177245] font-medium"><Check size={18} /> Your dispute letter PDF is ready.</div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button data-testid="download-pdf-btn" onClick={downloadPdf} className="rounded-xl h-12 bg-navy hover:bg-navy-hover text-white"><Download size={18} className="mr-2" /> Download PDF</Button>
                <Button data-testid="share-letter-btn" variant="outline" onClick={share} className="rounded-xl h-12"><Share2 size={18} className="mr-2" /> Share read-only link</Button>
                <Button data-testid="regenerate-pdf-btn" variant="ghost" onClick={generatePdf} disabled={pdfState === "pending"} className="rounded-xl h-12 text-brand"><RefreshCw size={16} className="mr-2" /> Regenerate</Button>
              </div>
              {shareUrl && (
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] p-3" data-testid="share-url-box">
                  <input readOnly value={shareUrl} className="flex-1 bg-transparent text-sm text-[#475569] outline-none" />
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="text-brand"><Copy size={16} /></button>
                </div>
              )}
              <ul className="mt-5 space-y-1.5 text-sm text-[#475569]" data-testid="pdf-checklist">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Sign the letter before sending.</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Keep a copy for your records.</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Send via a trackable method (certified mail / email).</li>
              </ul>
              {pdfDownloaded && <FeedbackPrompt context="letter_generated" />}
            </div>
          )}
          <div className="mt-6"><Button data-testid="step4-back-btn" variant="outline" onClick={() => setStep(3)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back to editor</Button></div>
        </div>
      )}
    </AppShell>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-sm font-medium text-navy">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
    </div>
  );
}
